import { Check, Copy } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { buildInstallSnippet } from '@/lib/install-snippet';

/**
 * Copy-to-clipboard falls back to selecting the text when the Clipboard API is
 * unavailable, which is the case on http origins and in some embedded browsers.
 */
export const InstallSnippet = ({ publicKey }: { publicKey: string }) => {
  const [copied, setCopied] = React.useState(false);
  const preRef = React.useRef<HTMLPreElement>(null);

  const snippet = buildInstallSnippet(publicKey);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const node = preRef.current;
      if (!node) {
        return;
      }
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  return (
    <div className="relative">
      <pre
        ref={preRef}
        className="overflow-x-auto rounded-lg border border-border bg-surface-muted p-4 pr-14 font-mono text-xs leading-relaxed text-ink"
      >
        <code>{snippet}</code>
      </pre>

      <Button
        variant="secondary"
        size="icon"
        className="absolute right-2 top-2 size-8"
        onClick={handleCopy}
        aria-label="Copy install snippet"
      >
        {copied ? <Check className="text-positive" /> : <Copy />}
      </Button>
    </div>
  );
};
